import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useGetUser } from "../../api/queries/useGetUser";
import { useAppContext } from "../../Context/AppContext";
import {
  findMembershipByName,
  formSaveIndicatesPaymentComplete,
} from "../Registration/registrationMembershipSync";
import {
  isEarlyStageMembership,
  isYouthOrEarlyStageMembership,
} from "../../utils/membershipTier";
import StepsProgressBar from "../Registration/StepsProgressBar";
import MembershipStep from "../Registration/RegistrationsSteps/MemberShipStep";
import TermsStep from "../Registration/RegistrationsSteps/TermsStep";
import CheckoutStep from "../Registration/RegistrationsSteps/CheckoutStep";
import "../css/RegisterForm.css";
import { useTranslation } from "react-i18next";

interface FormData {
  membershipName: string;
  isAnnualMembership: number;
  isTermConditions: number;
  isDirectory: number;
  discountCode?: string;
}

function resolveUpgradePlan(planParam: string | null): string | null {
  const raw = (planParam || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw.includes("executive")) return "Executive Membership";
  if (raw.includes("business")) return "Business Membership";
  if (raw.includes("early")) return "Early Stage Membership";
  if (raw.includes("youth")) return "Youth Membership";
  return null;
}

const RenewMembership: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { objectId } = useAppContext();
  const { data: memberships } = useGetMemberships();
  const { data: userDataResponse } = useGetUser({ id: objectId || "", enabled: !!objectId });
  const preferredPlan = useMemo(
    () => resolveUpgradePlan(searchParams.get("plan")),
    [searchParams]
  );

  const [step, setStep] = useState(0);
  const [membershipPrice, setMembershipPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [formData, setFormData] = useState<FormData>({
    membershipName: preferredPlan || "Business Membership",
    isAnnualMembership: 0,
    isTermConditions: 0,
    isDirectory: 0,
  });

  const stepLabels = [
    t("membership.selectMembership"),
    t("membership.step.terms"),
    t("membership.step.payment"),
    t("membership.step.confirmation"),
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const user = userDataResponse?.user;

  const userDataForCheckout = useMemo(
    () =>
      user
        ? {
            ...user,
            objectId: objectId || "",
            membershipName: formData.membershipName,
            isAnnualMembership: formData.isAnnualMembership,
            discountCode,
          }
        : null,
    [
      user,
      objectId,
      formData.membershipName,
      formData.isAnnualMembership,
      discountCode,
    ]
  );

  useEffect(() => {
    if (!user) return;
    // Prefer deep-linked upgrade plan (e.g. from attendee Connect lock);
    // otherwise keep the user's current membership as default.
    // Billing cycle defaults to monthly; do not overwrite a user toggle.
    setFormData((prev) => ({
      ...prev,
      membershipName:
        preferredPlan || user.membershipName || prev.membershipName,
    }));
  }, [user, preferredPlan]);

  useEffect(() => {
    if (step === 2 && userDataForCheckout) {
      localStorage.setItem("UserData", JSON.stringify(userDataForCheckout));
    }
  }, [step, userDataForCheckout]);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleMembershipNext = (data: Partial<FormData>, price: number) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setMembershipPrice(price);
    nextStep();
  };

  const handleTermsNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  const handleCheckoutNext = () => {
    nextStep();
  };

  const { mutate: saveUserMutate, isPending: isSaveUserPending } = useSaveUser({
    onSuccess: (_data, variables) => {
      if (!formSaveIndicatesPaymentComplete(variables)) {
        return;
      }
      if (objectId) {
        queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      }
      setStep(3);
    },
  });

  const membershipsList = useMemo(
    () =>
      Array.isArray(memberships)
        ? memberships
        : memberships
          ? [memberships]
          : [],
    [memberships],
  );

  // Disable current plan only when user has ACTIVE membership (upgrade/downgrade). When renewing after cancel, allow selecting same plan.
  const isMembershipActive =
    !!user &&
    user.status !== "InActive" &&
    user.stripeStatus?.toLowerCase() !== "canceled" &&
    user.stripeStatus?.toLowerCase() !== "cancelled";
  const currentMembershipToDisable = useMemo(() => {
    if (!user || !isMembershipActive) return undefined;
    const raw = user.membershipName?.trim();
    if (!raw) return undefined;
    const catalogMatch = findMembershipByName(membershipsList, raw);
    if (catalogMatch?.name) return catalogMatch.name;
    if (isYouthOrEarlyStageMembership(raw)) {
      const earlyStageRow = membershipsList.find((m) =>
        isYouthOrEarlyStageMembership(m.name),
      );
      if (earlyStageRow) return earlyStageRow.name;
    }
    return raw;
  }, [user, isMembershipActive, membershipsList]);

  if (!user) {
    return null;
  }

  return (
    <div className="p-4 rounded border-0 shadow d-flex flex-column">
      <button
        type="button"
        className="btn btn-outline-secondary mb-2 align-self-start"
        onClick={() => navigate("/manage-membership")}
      >
        {t("auth.back")}
      </button>
      <h4 className="mb-3">{t("membership.renewUpgradeTitle")}</h4>
      <StepsProgressBar currentStep={step} labels={stepLabels} />

      {step === 0 && (
        <MembershipStep
          key={`renew-${currentMembershipToDisable ?? "none"}-${membershipsList.length}`}
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          previewUserId={objectId?.trim() || undefined}
          previewEmail={user.email}
          previewName={`${user.firstName} ${user.lastName}`.trim()}
          defaultValues={{
            membershipName: formData.membershipName,
            isAnnualMembership: formData.isAnnualMembership,
          }}
          memberships={membershipsList}
          setMembershipPrice={setMembershipPrice}
          onNext={handleMembershipNext}
          onBack={() => navigate("/manage-membership")}
          currentMembershipName={currentMembershipToDisable}
        />
      )}

      {step === 1 && (
        <TermsStep
          defaultValues={{
            isTermConditions: formData.isTermConditions,
            isDirectory: formData.isDirectory,
          }}
          onNext={handleTermsNext}
          onBack={prevStep}
        />
      )}

      {step === 2 && (
        <CheckoutStep
          selectedMembership={formData.membershipName}
          membershipPrice={membershipPrice}
          memberships={membershipsList}
          isAnnualMembership={formData.isAnnualMembership === 1 ? "annual" : "monthly"}
          onNext={handleCheckoutNext}
          onBack={prevStep}
          discountCode={discountCode}
          saveUserMutate={saveUserMutate}
          isSaveUserPending={isSaveUserPending}
          skipPrePaymentMembershipSync
          earlyStageDirectStandardPath={isEarlyStageMembership(
            formData.membershipName,
          )}
        />
      )}

      {step === 3 && (
        <div className="p-4 text-center">
          <h5 className="text-success mb-3">{t("membership.renewSuccessTitle")}</h5>
          <p className="text-muted mb-4">
            {t("membership.renewSuccessText")}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/dashboard")}
          >
            {t("dashboard.goTo", { item: t("sidebar.dashboard") })}
          </button>
        </div>
      )}
    </div>
  );
};

export default RenewMembership;
